import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  Search,
  Plus,
  Kanban,
  List,
  User,
  Calendar,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  X,
  AlertCircle,
  GripVertical,
} from "lucide-react";
import MiniTabBar, { MiniTab } from "../ui/MiniTabBar";
import { mattersApi, pipelineStagesApi, PaginatedResponse } from "../../services/api";
import SampleBadge from "../ui/SampleBadge";
import CreatePracticeAreaCaseModal, { CaseCreationConfig } from "./CreatePracticeAreaCaseModal";
import { useMatterInspector } from "../../contexts/MatterInspectorContext";

// ── Types ─────────────────────────────────────────────────────────────

export interface Matter {
  _id: string;
  matterNumber: string;
  title: string;
  practiceAreas?: string[];
  status: string;
  pipelineStageId?: { _id: string; key: string; label: string; color: string } | string;
  clientId: { _id: string; displayName: string } | string;
  responsibleLawyerId: { _id: string; name: string } | string;
  openedAt: string;
  isSample?: boolean;
}

export interface PipelineStage {
  _id: string;
  firmId: string;
  pipelineType: string;
  key: string;
  label: string;
  color: string;
  order: number;
  isDefault: boolean;
}

export interface PracticeAreaPageConfig {
  /** e.g. "Criminal Defence" */
  title: string;
  /** Lucide icon component */
  icon: React.FC<any>;
  /** practiceArea value to filter matters, e.g. "criminal" */
  practiceAreaKey: string;
  /** Pipeline type for stages, e.g. "criminal" */
  pipelineType: string;
  /** Accent color class, e.g. "text-red-600" */
  accentColor?: string;
  /** Accent hex color for dynamic styling, e.g. "#dc2626" */
  accentHex?: string;
  /** Background class for header icon, e.g. "bg-red-50" */
  iconBg?: string;
  /** Extra mini-tabs beyond Dashboard/Pipeline/Table */
  extraTabs?: MiniTab[];
  /** Render extra tab content */
  renderExtraTab?: (tabKey: string, matters: Matter[]) => React.ReactNode;
  /** Case creation config  -  enables the "New Case" modal */
  caseConfig?: CaseCreationConfig;
  /** Custom create modal renderer. When provided, replaces the default CreatePracticeAreaCaseModal */
  renderCreateModal?: (
    onClose: () => void,
    onCreated: (matterId: string, meta?: any) => void
  ) => React.ReactNode;
}

// ── Helpers ───────────────────────────────────────────────────────────

const getClientName = (c: any) => (typeof c === "object" ? c?.displayName : "Unknown");
const getLawyerName = (l: any) => (typeof l === "object" ? l?.name : "Unknown");

const statusColors: Record<string, string> = {
  open: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-600",
  on_hold: "bg-amber-100 text-amber-700",
};

const formatStatus = (s: string) =>
  s === "on_hold" ? "On Hold" : s === "open" ? "Active" : s?.charAt(0).toUpperCase() + s?.slice(1);

// ── Pipeline Card ─────────────────────────────────────────────────────

const PipelineCard: React.FC<{
  matter: Matter;
  index: number;
  onClick: () => void;
}> = React.memo(({ matter, index, onClick }) => (
  <Draggable draggableId={matter._id} index={index}>
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        onClick={onClick}
        className={`bg-white rounded-xl border cursor-grab active:cursor-grabbing ${
          snapshot.isDragging
            ? "shadow-2xl border-brand-light ring-2 ring-brand-light scale-[1.02]"
            : "border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300"
        }`}
        style={provided.draggableProps.style}
      >
        <div className="p-3.5">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-[10px] font-mono text-slate-400">
              {matter.matterNumber}
            </p>
            {matter.isSample && <SampleBadge />}
          </div>
          <h4 className="text-sm font-semibold text-slate-800 truncate mb-2">{matter.title}</h4>
          <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
            <User className="h-3 w-3 flex-shrink-0" style={{ color: "var(--color-accent)" }} />
            <span className="truncate">{getClientName(matter.clientId)}</span>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <span className="text-xs text-slate-500 truncate">{getLawyerName(matter.responsibleLawyerId)}</span>
            {matter.openedAt && (
              <span className="text-[10px] text-slate-400">
                {new Date(matter.openedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
            )}
          </div>
        </div>
      </div>
    )}
  </Draggable>
));

PipelineCard.displayName = "PipelineCard";

// ── Headless Tab Content ──────────────────────────────────────────────

export interface PracticeAreaTabContentProps {
  practiceAreaKey: string;
  pipelineType: string;
  accentColor?: string;
  iconBg?: string;
  caseConfig?: CaseCreationConfig;
  renderCreateModal?: (
    onClose: () => void,
    onCreated: (matterId: string, meta?: any) => void
  ) => React.ReactNode;
  extraTabs?: MiniTab[];
  renderExtraTab?: (tabKey: string, matters: Matter[]) => React.ReactNode;
}

export const PracticeAreaTabContent: React.FC<PracticeAreaTabContentProps> = ({
  practiceAreaKey,
  pipelineType,
  accentColor = "text-brand",
  iconBg = "bg-brand-light",
  caseConfig,
  renderCreateModal,
  extraTabs = [],
  renderExtraTab,
}) => {
  const navigate = useNavigate();
  const { openMatterInspector } = useMatterInspector();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [matters, setMatters] = useState<Matter[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 200, total: 0, pages: 0 });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const initialLoadDone = useRef(false);

  const tabs: MiniTab[] = useMemo(
    () => [
      { key: "dashboard", label: "Dashboard", icon: <Kanban className="h-4 w-4" /> },
      { key: "table", label: "Table", icon: <List className="h-4 w-4" /> },
      ...extraTabs,
    ],
    [extraTabs]
  );

  // Fetch matters filtered by practice area
  const fetchMatters = useCallback(async () => {
    if (!initialLoadDone.current) setLoading(true);
    try {
      const params: Record<string, any> = {
        practiceArea: practiceAreaKey,
        limit: 200,
      };
      if (search) params.search = search;

      const res = await mattersApi.list(params);
      const data = res.data as PaginatedResponse<Matter>;
      setMatters(data.data);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to fetch matters:", err);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [practiceAreaKey, search]);

  // Fetch pipeline stages
  const fetchStages = useCallback(async () => {
    try {
      const res = await pipelineStagesApi.list(pipelineType);
      const data = res.data?.data || res.data || [];
      setStages(Array.isArray(data) ? data.sort((a: PipelineStage, b: PipelineStage) => a.order - b.order) : []);
    } catch (err) {
      console.error("Failed to fetch pipeline stages:", err);
    }
  }, [pipelineType]);

  useEffect(() => {
    fetchMatters();
    fetchStages();
  }, [fetchMatters, fetchStages]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Group matters by pipeline stage
  const groupedByStage = useMemo(() => {
    const grouped: Record<string, Matter[]> = {};
    for (const stage of stages) {
      grouped[stage._id] = [];
    }
    grouped["_unassigned"] = [];

    for (const m of matters) {
      const stageId =
        typeof m.pipelineStageId === "object" ? m.pipelineStageId?._id : m.pipelineStageId;
      if (stageId && grouped[stageId]) {
        grouped[stageId].push(m);
      } else {
        grouped["_unassigned"].push(m);
      }
    }
    return grouped;
  }, [matters, stages]);

  // Unified drag-drop handler for both columns and cards
  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      const { destination, source, draggableId, type } = result;
      if (!destination) return;

      // ── Column reorder ──
      if (type === "COLUMN") {
        if (destination.index === source.index) return;

        const reordered = [...stages];
        const [moved] = reordered.splice(source.index, 1);
        reordered.splice(destination.index, 0, moved);

        const updated = reordered.map((s, i) => ({ ...s, order: i }));
        setStages(updated);

        try {
          await pipelineStagesApi.reorder(
            updated.map((s) => ({ _id: s._id, order: s.order }))
          );
        } catch (err) {
          console.error("Failed to reorder columns:", err);
          setStages(stages);
        }
        return;
      }

      // ── Card move between columns ──
      if (destination.droppableId === "_unassigned") return;

      const newStageId = destination.droppableId;
      const prevMatters = [...matters];

      setMatters((prev) =>
        prev.map((m) =>
          m._id === draggableId
            ? { ...m, pipelineStageId: newStageId }
            : m
        )
      );

      try {
        await mattersApi.update(draggableId, { pipelineStageId: newStageId });
      } catch (err) {
        console.error("Failed to update pipeline stage:", err);
        setMatters(prevMatters);
      }
    },
    [matters, stages]
  );

  // Handle case created from modal
  const handleCaseCreated = useCallback(
    (matterId: string) => {
      setShowCreateModal(false);
      navigate(`/matters/${matterId}`);
    },
    [navigate]
  );

  return (
    <div className="flex flex-col">
      {/* Mini Tab Bar */}
      <MiniTabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        layoutId={`${practiceAreaKey}-tab-content`}
      />

      {/* Search Bar (visible on dashboard & table tabs) */}
      {(activeTab === "dashboard" || activeTab === "table") && (
        <div className="mb-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search matters..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div>
        <div>
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          )}

          {/* ── Dashboard Tab (Kanban Board) ────────────────────── */}
          {!loading && activeTab === "dashboard" && (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="board" type="COLUMN" direction="horizontal">
                {(boardProvided) => (
                  <div
                    ref={boardProvided.innerRef}
                    {...boardProvided.droppableProps}
                    className="flex gap-4 overflow-x-auto pb-4"
                    style={{ minHeight: "400px" }}
                  >
                    {stages.map((stage, colIndex) => (
                      <Draggable key={stage._id} draggableId={`col-${stage._id}`} index={colIndex}>
                        {(colProvided, colSnapshot) => (
                          <div
                            ref={colProvided.innerRef}
                            {...colProvided.draggableProps}
                            className={`flex-shrink-0 w-[280px] flex flex-col max-h-[calc(100vh-280px)] ${
                              colSnapshot.isDragging ? "opacity-90 shadow-2xl" : ""
                            }`}
                          >
                            {/* Column header  -  drag handle */}
                            <div
                              {...colProvided.dragHandleProps}
                              className="rounded-t-xl px-4 py-3 border-b-2 bg-white flex-shrink-0 cursor-grab active:cursor-grabbing"
                              style={{ borderBottomColor: stage.color || "#94a3b8" }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <GripVertical className="h-3.5 w-3.5 text-slate-300" />
                                  <div
                                    className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: stage.color || "#94a3b8" }}
                                  />
                                  <h3 className="text-sm font-semibold text-slate-700">{stage.label}</h3>
                                </div>
                                <span
                                  className="text-xs font-bold px-2 py-0.5 rounded-full"
                                  style={{
                                    backgroundColor: `${stage.color || "#94a3b8"}20`,
                                    color: stage.color || "#64748b",
                                  }}
                                >
                                  {(groupedByStage[stage._id] || []).length}
                                </span>
                              </div>
                            </div>

                            {/* Droppable area for cards */}
                            <Droppable droppableId={stage._id} type="CARD">
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  className={`flex-1 rounded-b-xl p-2 min-h-[120px] space-y-2 overflow-y-auto transition-colors duration-200 ${
                                    snapshot.isDraggingOver
                                      ? "bg-brand-light/70 ring-2 ring-inset ring-brand-light/50"
                                      : "bg-slate-50/80"
                                  }`}
                                >
                                  {(groupedByStage[stage._id] || []).map((matter, index) => (
                                    <PipelineCard
                                      key={matter._id}
                                      matter={matter}
                                      index={index}
                                      onClick={() => openMatterInspector(matter._id)}
                                    />
                                  ))}
                                  {provided.placeholder}
                                  {(groupedByStage[stage._id] || []).length === 0 && !snapshot.isDraggingOver && (
                                    <div className="flex flex-col items-center justify-center py-6 text-slate-300">
                                      <Briefcase className="h-5 w-5 mb-1" />
                                      <span className="text-xs">Drag matters here</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </Droppable>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {boardProvided.placeholder}

                    {/* Unassigned column (not draggable) */}
                    {(groupedByStage["_unassigned"] || []).length > 0 && (
                      <div className="flex-shrink-0 w-[280px] flex flex-col max-h-[calc(100vh-280px)]">
                        <div className="rounded-t-xl px-4 py-3 border-b-2 border-slate-300 bg-white flex-shrink-0">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-slate-400" />
                            <h3 className="text-sm font-semibold text-slate-500">Unassigned</h3>
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                              {groupedByStage["_unassigned"].length}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 rounded-b-xl p-2 min-h-[120px] space-y-2 overflow-y-auto bg-slate-50/80">
                          {groupedByStage["_unassigned"].map((matter) => (
                            <div
                              key={matter._id}
                              onClick={() => openMatterInspector(matter._id)}
                              className="bg-white rounded-xl border border-slate-200 shadow-sm p-3.5 cursor-pointer hover:shadow-md transition-all"
                            >
                              <p className="text-[10px] font-mono text-slate-400 mb-0.5">
                                {matter.matterNumber}
                              </p>
                              <h4 className="text-sm font-semibold text-slate-800 truncate">{matter.title}</h4>
                              <p className="text-xs text-slate-500 mt-1">{getClientName(matter.clientId)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {stages.length === 0 && (
                      <div className="flex-1 flex items-center justify-center py-16 text-slate-400">
                        <div className="text-center">
                          <Kanban className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                          <p className="font-medium">No pipeline stages configured</p>
                          <p className="text-sm mt-1">Set up pipeline stages in Settings to use the kanban board</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}

          {/* ── Table Tab ──────────────────────────────────────── */}
          {!loading && activeTab === "table" && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Matter/File #</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Title</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">Client</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">Lawyer</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden xl:table-cell">Opened</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matters.map((matter) => (
                      <tr
                        key={matter._id}
                        onClick={() => openMatterInspector(matter._id)}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 text-sm font-mono text-slate-500">
                          {matter.matterNumber}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800">{matter.title}</span>
                            {matter.isSample && <SampleBadge />}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 hidden md:table-cell">
                          {getClientName(matter.clientId)}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                              statusColors[matter.status] || "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {formatStatus(matter.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 hidden lg:table-cell">
                          {getLawyerName(matter.responsibleLawyerId)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500 hidden xl:table-cell">
                          {matter.openedAt
                            ? new Date(matter.openedAt).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : " - "}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {matters.length === 0 && (
                <div className="p-12 text-center text-slate-400">
                  <Briefcase className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                  <p className="font-medium">No matters found</p>
                </div>
              )}
            </div>
          )}

          {/* ── Extra Tabs (practice-specific) ─────────────────── */}
          {!loading && renderExtraTab && !["dashboard", "pipeline", "table"].includes(activeTab) && (
            <>{renderExtraTab(activeTab, matters)}</>
          )}
        </div>
      </div>

      {/* Create Case Modal */}
      {showCreateModal && (renderCreateModal
        ? renderCreateModal(() => setShowCreateModal(false), handleCaseCreated)
        : caseConfig && (
          <CreatePracticeAreaCaseModal
            config={caseConfig}
            title={practiceAreaKey}
            icon={Briefcase}
            onClose={() => setShowCreateModal(false)}
            onCreated={handleCaseCreated}
          />
        )
      )}
    </div>
  );
};

// ── Main Template ─────────────────────────────────────────────────────

const PracticeAreaPageTemplate: React.FC<PracticeAreaPageConfig> = ({
  title,
  icon: Icon,
  practiceAreaKey,
  pipelineType,
  accentColor = "text-brand",
  accentHex,
  iconBg = "bg-brand-light",
  extraTabs = [],
  renderExtraTab,
  caseConfig,
  renderCreateModal,
}) => {
  const navigate = useNavigate();
  const { openMatterInspector } = useMatterInspector();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [matters, setMatters] = useState<Matter[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 200, total: 0, pages: 0 });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const initialLoadDone = useRef(false);

  const tabs: MiniTab[] = useMemo(
    () => [
      { key: "dashboard", label: "Dashboard", icon: <Kanban className="h-4 w-4" /> },
      { key: "table", label: "Table", icon: <List className="h-4 w-4" /> },
      ...extraTabs,
    ],
    [extraTabs]
  );

  // Fetch matters filtered by practice area
  const fetchMatters = useCallback(async () => {
    if (!initialLoadDone.current) setLoading(true);
    try {
      const params: Record<string, any> = {
        practiceArea: practiceAreaKey,
        limit: 200,
      };
      if (search) params.search = search;

      const res = await mattersApi.list(params);
      const data = res.data as PaginatedResponse<Matter>;
      setMatters(data.data);
      setPagination((prev) => ({ ...prev, ...data.pagination }));
    } catch (err) {
      console.error("Failed to fetch matters:", err);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [practiceAreaKey, search]);

  // Fetch pipeline stages
  const fetchStages = useCallback(async () => {
    try {
      const res = await pipelineStagesApi.list(pipelineType);
      const data = res.data?.data || res.data || [];
      setStages(Array.isArray(data) ? data.sort((a: PipelineStage, b: PipelineStage) => a.order - b.order) : []);
    } catch (err) {
      console.error("Failed to fetch pipeline stages:", err);
    }
  }, [pipelineType]);

  useEffect(() => {
    fetchMatters();
    fetchStages();
  }, [fetchMatters, fetchStages]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Group matters by pipeline stage
  const groupedByStage = useMemo(() => {
    const grouped: Record<string, Matter[]> = {};
    for (const stage of stages) {
      grouped[stage._id] = [];
    }
    // "Unassigned" bucket for matters without a pipeline stage
    grouped["_unassigned"] = [];

    for (const m of matters) {
      const stageId =
        typeof m.pipelineStageId === "object" ? m.pipelineStageId?._id : m.pipelineStageId;
      if (stageId && grouped[stageId]) {
        grouped[stageId].push(m);
      } else {
        grouped["_unassigned"].push(m);
      }
    }
    return grouped;
  }, [matters, stages]);

  // Unified drag-drop handler for both columns and cards
  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      const { destination, source, draggableId, type } = result;
      if (!destination) return;

      // ── Column reorder ──
      if (type === "COLUMN") {
        if (destination.index === source.index) return;

        const reordered = [...stages];
        const [moved] = reordered.splice(source.index, 1);
        reordered.splice(destination.index, 0, moved);

        // Optimistic update
        const updated = reordered.map((s, i) => ({ ...s, order: i }));
        setStages(updated);

        try {
          await pipelineStagesApi.reorder(
            updated.map((s) => ({ _id: s._id, order: s.order }))
          );
        } catch (err) {
          console.error("Failed to reorder columns:", err);
          // Revert
          setStages(stages);
        }
        return;
      }

      // ── Card move between columns ──
      if (destination.droppableId === "_unassigned") return;

      const newStageId = destination.droppableId;
      const prevMatters = [...matters];

      // Optimistic update
      setMatters((prev) =>
        prev.map((m) =>
          m._id === draggableId
            ? { ...m, pipelineStageId: newStageId }
            : m
        )
      );

      try {
        await mattersApi.update(draggableId, { pipelineStageId: newStageId });
      } catch (err) {
        console.error("Failed to update pipeline stage:", err);
        setMatters(prevMatters);
      }
    },
    [matters, stages]
  );

  // Handle case created from modal
  const handleCaseCreated = useCallback(
    (matterId: string) => {
      setShowCreateModal(false);
      navigate(`/matters/${matterId}`);
    },
    [navigate]
  );

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className={`h-12 w-12 rounded-xl ${iconBg} flex items-center justify-center`}>
            <Icon className={`h-6 w-6 ${accentColor}`} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{title}</h1>
            <p className="text-sm text-slate-500">
              {matters.length} matter{matters.length !== 1 ? "s" : ""} &middot; {matters.filter((m) => m.status === "open").length} active
            </p>
          </div>
        </div>
        <button
          onClick={() => (caseConfig || renderCreateModal ? setShowCreateModal(true) : navigate("/matters"))}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all border shadow-sm hover:shadow-md"
          style={{
            color: accentHex || "var(--color-primary)",
            borderColor: accentHex ? `${accentHex}40` : "var(--color-primary)",
            backgroundColor: "white",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = accentHex || "var(--color-primary)";
            e.currentTarget.style.borderColor = accentHex || "var(--color-primary)";
            e.currentTarget.style.color = "white";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "white";
            e.currentTarget.style.borderColor = accentHex ? `${accentHex}40` : "var(--color-primary)";
            e.currentTarget.style.color = accentHex || "var(--color-primary)";
          }}
        >
          <Plus className="h-4 w-4" />
          New {title} Matter
        </button>
      </div>

      {/* Mini Tab Bar */}
      <MiniTabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        layoutId={`${practiceAreaKey}-tabs`}
      />

      {/* Search Bar (visible on dashboard & table tabs) */}
      {(activeTab === "dashboard" || activeTab === "table") && (
        <div className="mb-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search matters..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
            />
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div>
        <div>
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          )}

          {/* ── Dashboard Tab (Kanban Board) ────────────────────── */}
          {!loading && activeTab === "dashboard" && (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="board" type="COLUMN" direction="horizontal">
                {(boardProvided) => (
                  <div
                    ref={boardProvided.innerRef}
                    {...boardProvided.droppableProps}
                    className="flex gap-4 overflow-x-auto pb-4"
                    style={{ minHeight: "400px" }}
                  >
                    {stages.map((stage, colIndex) => (
                      <Draggable key={stage._id} draggableId={`col-${stage._id}`} index={colIndex}>
                        {(colProvided, colSnapshot) => (
                          <div
                            ref={colProvided.innerRef}
                            {...colProvided.draggableProps}
                            className={`flex-shrink-0 w-[280px] flex flex-col max-h-[calc(100vh-280px)] ${
                              colSnapshot.isDragging ? "opacity-90 shadow-2xl" : ""
                            }`}
                          >
                            {/* Column header  -  drag handle */}
                            <div
                              {...colProvided.dragHandleProps}
                              className="rounded-t-xl px-4 py-3 border-b-2 bg-white flex-shrink-0 cursor-grab active:cursor-grabbing"
                              style={{ borderBottomColor: stage.color || "#94a3b8" }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <GripVertical className="h-3.5 w-3.5 text-slate-300" />
                                  <div
                                    className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: stage.color || "#94a3b8" }}
                                  />
                                  <h3 className="text-sm font-semibold text-slate-700">{stage.label}</h3>
                                </div>
                                <span
                                  className="text-xs font-bold px-2 py-0.5 rounded-full"
                                  style={{
                                    backgroundColor: `${stage.color || "#94a3b8"}20`,
                                    color: stage.color || "#64748b",
                                  }}
                                >
                                  {(groupedByStage[stage._id] || []).length}
                                </span>
                              </div>
                            </div>

                            {/* Droppable area for cards */}
                            <Droppable droppableId={stage._id} type="CARD">
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  className={`flex-1 rounded-b-xl p-2 min-h-[120px] space-y-2 overflow-y-auto transition-colors duration-200 ${
                                    snapshot.isDraggingOver
                                      ? "bg-brand-light/70 ring-2 ring-inset ring-brand-light/50"
                                      : "bg-slate-50/80"
                                  }`}
                                >
                                  {(groupedByStage[stage._id] || []).map((matter, index) => (
                                    <PipelineCard
                                      key={matter._id}
                                      matter={matter}
                                      index={index}
                                      onClick={() => openMatterInspector(matter._id)}
                                    />
                                  ))}
                                  {provided.placeholder}
                                  {(groupedByStage[stage._id] || []).length === 0 && !snapshot.isDraggingOver && (
                                    <div className="flex flex-col items-center justify-center py-6 text-slate-300">
                                      <Briefcase className="h-5 w-5 mb-1" />
                                      <span className="text-xs">Drag matters here</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </Droppable>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {boardProvided.placeholder}

                    {/* Unassigned column (not draggable) */}
                    {(groupedByStage["_unassigned"] || []).length > 0 && (
                      <div className="flex-shrink-0 w-[280px] flex flex-col max-h-[calc(100vh-280px)]">
                        <div className="rounded-t-xl px-4 py-3 border-b-2 border-slate-300 bg-white flex-shrink-0">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-slate-400" />
                            <h3 className="text-sm font-semibold text-slate-500">Unassigned</h3>
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                              {groupedByStage["_unassigned"].length}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 rounded-b-xl p-2 min-h-[120px] space-y-2 overflow-y-auto bg-slate-50/80">
                          {groupedByStage["_unassigned"].map((matter) => (
                            <div
                              key={matter._id}
                              onClick={() => openMatterInspector(matter._id)}
                              className="bg-white rounded-xl border border-slate-200 shadow-sm p-3.5 cursor-pointer hover:shadow-md transition-all"
                            >
                              <p className="text-[10px] font-mono text-slate-400 mb-0.5">
                                {matter.matterNumber}
                              </p>
                              <h4 className="text-sm font-semibold text-slate-800 truncate">{matter.title}</h4>
                              <p className="text-xs text-slate-500 mt-1">{getClientName(matter.clientId)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {stages.length === 0 && (
                      <div className="flex-1 flex items-center justify-center py-16 text-slate-400">
                        <div className="text-center">
                          <Kanban className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                          <p className="font-medium">No pipeline stages configured</p>
                          <p className="text-sm mt-1">Set up pipeline stages in Settings to use the kanban board</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}

          {/* ── Table Tab ──────────────────────────────────────── */}
          {!loading && activeTab === "table" && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Matter/File #</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Title</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">Client</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">Lawyer</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden xl:table-cell">Opened</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matters.map((matter) => (
                      <tr
                        key={matter._id}
                        onClick={() => openMatterInspector(matter._id)}
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 text-sm font-mono text-slate-500">
                          {matter.matterNumber}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800">{matter.title}</span>
                            {matter.isSample && <SampleBadge />}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 hidden md:table-cell">
                          {getClientName(matter.clientId)}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                              statusColors[matter.status] || "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {formatStatus(matter.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600 hidden lg:table-cell">
                          {getLawyerName(matter.responsibleLawyerId)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500 hidden xl:table-cell">
                          {matter.openedAt
                            ? new Date(matter.openedAt).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : " - "}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {matters.length === 0 && (
                <div className="p-12 text-center text-slate-400">
                  <Briefcase className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                  <p className="font-medium">No matters found</p>
                </div>
              )}
            </div>
          )}

          {/* ── Extra Tabs (practice-specific) ─────────────────── */}
          {!loading && renderExtraTab && !["dashboard", "pipeline", "table"].includes(activeTab) && (
            <>{renderExtraTab(activeTab, matters)}</>
          )}
        </div>
      </div>

      {/* Create Case Modal */}
      {showCreateModal && (renderCreateModal
        ? renderCreateModal(() => setShowCreateModal(false), handleCaseCreated)
        : caseConfig && (
          <CreatePracticeAreaCaseModal
            config={caseConfig}
            title={title}
            icon={Icon}
            onClose={() => setShowCreateModal(false)}
            onCreated={handleCaseCreated}
          />
        )
      )}
    </div>
  );
};

export default PracticeAreaPageTemplate;
